import { Row } from 'gridjs';
import { min } from 'lodash';
import * as XLSX from 'xlsx';
import Verifiers from '../../utils/Verifiers.jsx';

/**
 * Lector de documentos excel desde un evento de input, actualiza la fucnion setExcel con el contenido del excel
 * @param {Object} event evento del input desde donde se lee e docuemnto excel
 * @param {function} setExcel actualizador de estado, se utiliza para actualizar las variables donde el componente gurdara el contenido del excel
 * @param {function} setLoader actualizador de estado, se utiliza si se envia 
 * @param {function} setDialog actualizador de estado, se utiliza para mostrar mensajes de error
 * @returns {null} se actualiza la funcion setExcel con el contenido del excel, el formato es { hojas: [], workbook: object | undefined }
 */
export const extractWorkbook = (event, setExcel, setDialog = false, setLoader = false) => {
    //recibe un evento con un doc excel, lo procesa y asigna el wordbook y los nombres de las hojas
    if (!setExcel) return;
    let resultados = { hojas: [], workbook: undefined };

    if (event.target.files == 0) return setExcel(resultados);;

    if (setLoader) setLoader(true);
    let selectedFile = event.target.files[0];

    if (!selectedFile) {
        if (setLoader) setLoader(false);
        return setExcel(resultados);;
    }
    if (selectedFile.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        if (setLoader) setLoader(false);
        if (setDialog) Verifiers.activeDialog(setDialog, "Archivo no válido, solo se aceptan hojas de calculo Excel", "Error", "error");
        return setExcel(resultados);;
    }

    let fileReader = new FileReader();
    fileReader.onload = (e) => {
        let data = new Uint8Array(e.target.result);
        let workbook = XLSX.read(data, { type: 'array' });
        if (setLoader) setLoader(false);
        return setExcel({ workbook: workbook, hojas: workbook.SheetNames });
    };

    fileReader.readAsArrayBuffer(selectedFile);
}

/**
 * Lee la hoja seleccionada dentro del archivo excel, si se encuentran datos los envia en una lista de objetos JSON
 * @param {XLSX.WorkSheet} workbook resultado de la lectura de un archivo excel
 * @param {string} hoja hoja a leer del archivo excel
 * @returns {Array} arreglo de objetos con los datos de la hoja seleccionada
 */
export const leerHojaExcel = (workbook, hoja) => {
    if (!workbook || !hoja || hoja == "") return [];

    let worksheet = workbook.Sheets[hoja];
    if (!worksheet) return [];
    let jsons = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    if (Array.isArray(jsons) && jsons.length > 0) return jsons;
    return [];
}

export const procesarJSONtemperaturas = (json, desfase = 0) => {
    if (!json || json.length == 0) return [];

    let length = json.length;
    let encabezados_esperados = ['fecha', 't max', 't min', 'et'];
    let encabezadosEncontrados = false;
    let encabezadosEindices = { encabezados: [], indices: [] };
    let inidiceEncabezados = -1;
    for (let i = 0; i < length; i++) {
        let { encabezados, indices } = extraerEncabezados(encabezados_esperados, json[i]);
        if (encabezados.length > 0) {
            encabezadosEncontrados = true;
            encabezadosEindices = { encabezados, indices };
            inidiceEncabezados = i + 1;
            break;
        }
    }
    if (!encabezadosEncontrados) return [];
    let datos = [];
    let conversorEncabezados = { 'fecha': 'fecha', 't max': 'maxTemperatura', 't min': 'minTemperatura', 'et': 'evapotranspiracion' };
    for (let i = inidiceEncabezados; i < length; i++) {
        let row = json[i];
        let data = {};

        encabezadosEindices.encabezados.forEach((element, index) => {
            let encConverso = conversorEncabezados[element];
            let indice = encabezadosEindices.indices[index];
            data[encConverso] = row[indice] ?? 0;
            if (encConverso === 'fecha') data[encConverso] = fechaNumericoExcelADateFromat(data[encConverso], desfase);
        });
        datos.push(data);
    }
    return datos;
}

/**
 * Verifica si una lista de encabezados se encuentra en una fila de un archivo excel, si se encuentran los devuelve junto con los indices donde se encontraron
 * @param {List} encabezados_esperados lista de encabezados que se esperan encontrar en la fila, deben estar nromalizados y en minusculas
 * @param {List} jsonRow datos json de una fila del excel
 * @returns {List} lista de encabezados encontrados en la fila y los indices donde fueron encontrados
 */
const extraerEncabezados = (encabezados_esperados, jsonRow) => {
    let len = jsonRow.length;
    let encabezados = [];
    let indices = [];
    for (let i = 0; i < len; i++) {
        let element = Verifiers.text_normalice(`${jsonRow[i]}`);
        if (encabezados_esperados.includes(element) && !encabezados.includes(element)) {
            encabezados.push(element);
            indices.push(i);
        }
    }
    return { encabezados, indices };
}

/**
 * Transforma una fecha del formato numerico que maneja excel  una fecha en formato 'YYYY-MM-DD'
 * @param {string | float | int} valorExcelOriginal valor numerico de una celda de excel que contiene una fecha en formato numerico
 * @returns String con la fecha en formato 'YYYY-MM-DD' o null si el valor no es valido
 */
const fechaNumericoExcelADateFromat = (valorExcelOriginal, desfase = 0) => {
    if (valorExcelOriginal === null || valorExcelOriginal === undefined) {
        return null; // Manejar el caso de valor nulo
    }
    let valorExcel = 0;
    let desfaseEnDias = desfase * 86400;
    try {
        valorExcel = parseInt(valorExcelOriginal);
        if (isNaN(valorExcel)) return null;
    } catch (error) {
        return null;
    }

    // Convertir a valor Unix Timestamp (segundos desde el 1 de enero de 1970)
    var unixTimestamp = (valorExcel - 25569) * 86400; // 86400 segundos en un día
    unixTimestamp += desfaseEnDias;

    // Crear un objeto de fecha en JavaScript
    var fecha = new Date(unixTimestamp * 1000); // Multiplicamos por 1000 porque JavaScript usa milisegundos

    // Obtener componentes de fecha (año, mes, día)
    var year = fecha.getFullYear();
    var month = String(fecha.getMonth() + 1).padStart(2, '0'); // Añadir cero inicial si es necesario
    var day = String(fecha.getDate()).padStart(2, '0'); // Añadir cero inicial si es necesario

    // Formatear la fecha como "YYYY-MM-DD"
    return `${year}-${month}-${day}`;
}